import api from './axios';

// ============================================
// Time Entry API - matches backend: /api/time-entries
// Controller: TimeEntryController.cs
// DTOs: TimeEntryDto, CreateTimeEntryRequest, UpdateTimeEntryRequest, StartTimeEntryRequest
// Response wrapper: Result<T> { isSuccess, message, data }
// Paginated: Result<PaginatedResult<T>> { isSuccess, message, data: { items, page, pageSize, totalCount } }
// ============================================

/**
 * Get all time entries (paginated)
 * GET /api/time-entries?page=1&pageSize=10
 */
export const getTimeEntries = async (page = 1, pageSize = 50, viewMode = false) => {
  try {
    const response = await api.get('/api/time-entries', { params: { page, pageSize, isConstellation : viewMode } });
    return response.data; // { isSuccess, message, data: { items: TimeEntryDto[], page, pageSize, totalCount } }
  } catch (error) {
    console.error('getTimeEntries error:', error.response?.data || error.message);
    return { isSuccess: false, data: null, message: error.message };
  }
};

/**
 * Get time entry by ID
 * GET /api/time-entries/{id}
 */
export const getTimeEntryById = async (id) => {
  try {
    const response = await api.get(`/api/time-entries/${id}`);
    return response.data;
  } catch (error) {
    console.error('getTimeEntryById error:', error.response?.data || error.message);
    return { isSuccess: false, data: null, message: error.message };
  }
};

/**
 * Get time entries by employee
 * GET /api/time-entries/employee/{employeeUuid}?page=1&pageSize=50
 */
export const getTimeEntriesByEmployee = async (employeeUuid, page = 1, pageSize = 10, viewMode = false) => {
  try {
    const response = await api.get(`/api/time-entries/employee/${employeeUuid}`, { params: { page, pageSize, isConstellation : viewMode } });
    return response.data;
  } catch (error) {
    console.error('getTimeEntriesByEmployee error:', error.response?.data || error.message);
    return { isSuccess: false, data: null, message: error.message };
  }
};

/**
 * Get all running entries for employee (supports multiple concurrent timers)
 * GET /api/time-entries/employee/{employeeUuid}/running
 * Returns: { isSuccess, data: TimeEntryDto[] }
 */
export const getRunningEntries = async (employeeUuid) => {
  try {
    const response = await api.get(`/api/time-entries/employee/${employeeUuid}/running`);
    return response.data;
  } catch (error) {
    // 404 or no running entries is not really an error
    if (error.response?.status === 404) {
      return { isSuccess: true, data: [], message: 'No running entries' };
    }
    return { isSuccess: false, data: [], message: error.message };
  }
};

/**
 * Create a new time entry (manual)
 * POST /api/time-entries
 * Body: CreateTimeEntryRequest { employeeUUId, employeeName, timeTypeCode, date, startTime, endTime, hours, description, referenceProjectId, referenceProductionTaskId, isRunning }
 */
export const createTimeEntry = async (payload) => {
  try {
    const response = await api.post('/api/time-entries', payload);
    return response.data;
  } catch (error) {
    console.error('createTimeEntry error:', error.response?.data || error.message);
    return {
      isSuccess: false,
      data: null,
      message: error.response?.data?.message || error.response?.data?.title || error.message || 'Failed to create time entry',
    };
  }
};

/**
 * Update a time entry
 * PUT /api/time-entries/{id}
 * Body: UpdateTimeEntryRequest { startTime, endTime, hours, description, syncStatus, sapErrorMessage, isRunning }
 */
export const updateTimeEntry = async (id, payload) => {
  try {
    const response = await api.put(`/api/time-entries/${id}`, payload);
    return response.data;
  } catch (error) {
    console.error('updateTimeEntry error:', error.response?.data || error.message);
    return {
      isSuccess: false,
      data: null,
      message: error.response?.data?.message || error.response?.data?.title || error.message || 'Failed to update time entry',
    };
  }
};

/**
 * Delete a time entry
 * DELETE /api/time-entries/{id}
 */
export const deleteTimeEntry = async (id) => {
  try {
    const response = await api.delete(`/api/time-entries/${id}`);
    return response.data;
  } catch (error) {
    console.error('deleteTimeEntry error:', error.response?.data || error.message);
    return {
      isSuccess: false,
      message: error.response?.data?.message || error.response?.data?.title || error.message || 'Failed to delete',
    };
  }
};

/**
 * Start a time entry (timer)
 * POST /api/time-entries/start
 * Body: StartTimeEntryRequest { employeeUUId, employeeName, timeTypeCode, date, referenceProjectId, referenceProductionTaskId, description }
 */
export const startTimeEntry = async (payload) => {
  try {
    const response = await api.post('/api/time-entries/start', payload);
    return response.data;
  } catch (error) {
    console.error('startTimeEntry error:', error.response?.data || error.message);
    return {
      isSuccess: false,
      data: null,
      message: error.response?.data?.message || error.response?.data?.title || error.message || 'Failed to start time entry',
    };
  }
};

/**
 * Stop a running time entry
 * POST /api/time-entries/{id}/stop
 * Body (optional): { notes: string }
 */
export const stopTimeEntry = async (id, notes = null, activityId = null, activityName = null) => {
  try {
    const body = {};
    if (notes) body.notes = notes;
    if (activityId) body.activityId = activityId;
    if (activityName) body.activityName = activityName;
    const response = await api.post(`/api/time-entries/${id}/stop`, body);
    return response.data;
  } catch (error) {
    console.error('stopTimeEntry error:', error.response?.data || error.message);
    return {
      isSuccess: false,
      data: null,
      message: error.response?.data?.message || error.response?.data?.title || error.message || 'Failed to stop time entry',
    };
  }
};

/**
 * Stop current running entry and start a new one atomically
 * POST /api/time-entries/stop-and-start
 */
export const stopAndStartTimeEntry = async (runningEntryId, newEntryPayload) => {
  try {
    const response = await api.post('/api/time-entries/stop-and-start', {
      runningEntryId,
      newEntry: newEntryPayload,
    });
    return response.data;
  } catch (error) {
    console.error('stopAndStartTimeEntry error:', error.response?.data || error.message);
    return {
      isSuccess: false,
      data: null,
      message: error.response?.data?.message || error.response?.data?.title || error.message || 'Failed to stop and start',
    };
  }
};

/**
 * Search production task by barcode number
 * GET /api/production-tasks/barcode/{barcodeNumber}
 */
export const getProductionTaskByBarcode = async (barcodeNumber) => {
  try {
    const response = await api.get(`/api/production-tasks/barcode/${barcodeNumber}`);
    return response.data; // { isSuccess, data: ProductionTaskDto }
  } catch (error) {
    console.error('getProductionTaskByBarcode error:', error.response?.data || error.message);
    return {
      isSuccess: false,
      data: null,
      message: error.response?.data?.message || 'Task not found for this barcode',
    };
  }
};

/**
 * Get past time entries for a production task (all users)
 * GET /api/time-entries/history/{productionTaskId}
 * Returns: { isSuccess, data: TimeEntryDto[] }
 */
export const getTimeEntryHistoryByTask = async (productionTaskId) => {
  try {
    const response = await api.get(`/api/time-entries/history/${encodeURIComponent(productionTaskId)}`);
    return response.data;
  } catch (error) {
    console.error('getTimeEntryHistoryByTask error:', error.response?.data || error.message);
    return { isSuccess: false, data: [], message: error.message };
  }
};

/**
 * Get all active production tasks
 * GET /api/production-tasks?page=1&pageSize=100
 */
export const getProductionTasks = async () => {
  try {
    const response = await api.get('/api/production-tasks', { params: { page: 1, pageSize: 100 } });
    return response.data; // { isSuccess, data: { items: ProductionTaskDto[], ... } }
  } catch (error) {
    console.error('getProductionTasks error:', error.response?.data || error.message);
    return { isSuccess: false, data: null, message: error.message };
  }
};

// ============================================
// SAP Activity Time APIs
// These pull start/end times from SAP ByDesign via QueryProductionLotISIIn
// The backend stores these in our DB (single source of truth)
// ============================================

/**
 * Get sync status (total/synced/pending/failed)
 * GET /api/sync/status
 */
export const getSyncStatus = async () => {
  try {
    const response = await api.get('/api/sync/status');
    return response.data;
  } catch (error) {
    console.error('getSyncStatus error:', error.response?.data || error.message);
    return { isSuccess: false, data: null, message: error.message };
  }
};

/**
 * Trigger sync for pending time entries to SAP
 * POST /api/sync/time-entries
 */
export const syncTimeEntriesToSap = async (entryIds = null) => {
  try {
    const response = await api.post('/api/sync/time-entries', { entryIds });
    return response.data;
  } catch (error) {
    console.error('syncTimeEntriesToSap error:', error.response?.data || error.message);
    return { isSuccess: false, data: null, message: error.message };
  }
};

/**
 * Trigger sync of activity start/end times FROM SAP
 * POST /api/sync/activity-times
 */
export const syncActivityTimesFromSap = async () => {
  try {
    const response = await api.post('/api/sync/activity-times');
    return response.data;
  } catch (error) {
    console.error('syncActivityTimesFromSap error:', error.response?.data || error.message);
    return { isSuccess: false, data: null, message: error.message };
  }
};

